import React, { useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Spinner, BlockStack, Text } from '@shopify/polaris';
import { AuthContext } from '../../context/AuthContext';

export default function OAuthRedirectHandler() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const { login, token: existingToken } = useContext(AuthContext);

  useEffect(() => {
    const params = new URLSearchParams(search);
    const token = params.get('token');
    const host = params.get('host');
    let next = params.get('next') || '/integrations';

    console.log('[OAuthRedirect] Received params:', { 
      hasToken: !!token, 
      next, 
      host,
      hasExistingToken: !!existingToken 
    });

    // =========================================================================
    // Handle authentication based on what we received
    // =========================================================================
    if (token) {
      // New token provided (login/signup flow) - save it
      console.log('[OAuthRedirect] New token received, saving...');
      login(token);
    } else if (existingToken) {
      // No new token but user is already authenticated (GA4 linking flow)
      console.log('[OAuthRedirect] GA4 linking complete, user already authenticated');
    } else {
      // No token and no existing auth - destination page will handle
      console.log('[OAuthRedirect] No token found, destination will handle auth');
    }

    // =========================================================================
    // Parse the next path - it may contain its own query parameters
    // e.g., next = "/install-custom-script?siteId=123&siteToken=abc&shopDomain=example.com"
    // =========================================================================
    let finalPath = next;
    
    try {
      // Check if next contains query params
      if (next.includes('?')) {
        const [pathname, queryString] = next.split('?');
        const nextParams = new URLSearchParams(queryString);
        
        // Add host if present and not already in next
        if (host && !nextParams.has('host')) {
          nextParams.set('host', host);
        }
        
        // Add token if we have one and destination might need it
        // (for pages like InstallCustomScript that handle their own token)
        if (token && !nextParams.has('token')) {
          nextParams.set('token', token);
        }
        
        finalPath = `${pathname}?${nextParams.toString()}`;
      } else {
        // Simple path without query params
        const nextParams = new URLSearchParams();
        if (host) nextParams.set('host', host);
        if (token) nextParams.set('token', token);
        
        finalPath = nextParams.toString() 
          ? `${next}?${nextParams.toString()}`
          : next;
      }
    } catch (e) {
      console.error('[OAuthRedirect] Error parsing next path:', e);
      finalPath = next;
    }

    console.log('[OAuthRedirect] Navigating to:', finalPath);

    // Strip out the oauth params from URL so it stays clean
    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, '', cleanUrl);

    // Navigate to the final path
    navigate(finalPath, { replace: true });

  }, [search, login, navigate, existingToken]);

  return (
    <div style={{ padding: 20, textAlign: 'center' }}>
      <BlockStack alignment="center" gap="base">
        <Spinner size="large" />
        <Text as="p">Connecting your account…</Text>
      </BlockStack>
    </div>
  );
}